import React, { FC, useEffect, useState } from 'react';
import { Button, Header, Message, Modal } from 'semantic-ui-react';

/** Only http(s) links are accepted, so a stored "javascript:" or other scheme can't run in the app. */
export const getSafeExternalLink = ( value?: string ): string | undefined => {
    const trimmed = value?.trim();
    if( !trimmed ) return undefined;

    try {
        const url = new URL( trimmed );
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : undefined;
    } catch {
        return undefined;
    }
};

/** Marks a version whose file lives in SharePoint rather than blob storage. */
export const SharepointBadge: FC = () => (
    <span className={ 'document-version-sharepoint-badge' } title={ 'Stored in SharePoint' } aria-label={ 'Stored in SharePoint' }>
        S
    </span>
);

/** Best-guess document name from the last path segment of a SharePoint URL (without extension). */
const getNameFromLink = ( link: string ): string => {
    try {
        const segments = new URL( link ).pathname.split( '/' ).filter( Boolean );
        const last = decodeURIComponent( segments[ segments.length - 1 ] || '' );
        const dotIndex = last.lastIndexOf( '.' );
        return dotIndex > 0 ? last.slice( 0, dotIndex ) : last;
    } catch {
        return '';
    }
};

interface SharepointLinkDialogProps {
    open: boolean;
    /** True when this creates a new document, so it needs a name. */
    requireDocumentName?: boolean;
    onClose: () => void;
    onSubmit: ( sharepointLink: string, documentName: string ) => Promise<void>;
}

const SharepointLinkDialog: FC<SharepointLinkDialogProps> = ( {
    open,
    requireDocumentName,
    onClose,
    onSubmit,
} ) => {
    const [ link, setLink ]                   = useState( '' ),
          [ documentName, setDocumentName ]   = useState( '' ),
          [ nameTouched, setNameTouched ]     = useState( false ),
          [ isSaving, setIsSaving ]           = useState( false ),
          [ errorMessage, setErrorMessage ]   = useState( '' );

    useEffect( () => {
        if( !open ) return;
        setLink( '' );
        setDocumentName( '' );
        setNameTouched( false );
        setErrorMessage( '' );
    }, [ open ] );

    const handleLinkChange = ( value: string ) => {
        setLink( value );
        if( requireDocumentName && !nameTouched ){
            setDocumentName( getNameFromLink( value.trim() ) );
        }
    };

    const handleClose = () => {
        if( isSaving ) return;
        onClose();
    };

    const handleSubmit = async () => {
        const safeLink = getSafeExternalLink( link );
        if( !safeLink ){
            setErrorMessage( 'Enter a valid link starting with https://' );
            return;
        }

        if( requireDocumentName && !documentName.trim() ){
            setErrorMessage( 'Document Name is required.' );
            return;
        }

        setIsSaving( true );
        setErrorMessage( '' );

        try {
            await onSubmit( safeLink, documentName.trim() );
            onClose();
        } catch( err ){
            setErrorMessage( err instanceof Error ? err.message : 'Saving the SharePoint link failed.' );
        } finally {
            setIsSaving( false );
        }
    };

    return (
        <Modal
            size={ 'small' }
            open={ open }
            onClose={ handleClose }
            closeOnDimmerClick={ !isSaving }
            closeOnEscape={ !isSaving }
            className={ 'sharepoint-link-dialog' }
        >
            <Header icon={ 'linkify' } content={ requireDocumentName ? 'Add SharePoint document' : 'Add SharePoint version' } />
            <Modal.Content>
                <p className={ 'sharepoint-link-dialog-hint' }>
                    { requireDocumentName
                        ? 'Paste the SharePoint link. A new document is created with this link as its first version.'
                        : 'Paste the SharePoint link. It is added as a new version and becomes the active version.' }
                </p>

                <div className={ 'sharepoint-link-dialog-field' }>
                    <label htmlFor={ 'sharepoint-link-dialog-url' }>SharePoint link</label>
                    <input
                        id={ 'sharepoint-link-dialog-url' }
                        type={ 'url' }
                        autoFocus
                        value={ link }
                        onChange={ e => handleLinkChange( e.target.value ) }
                        onKeyDown={ e => {
                            if( e.key === 'Enter' ) handleSubmit();
                        } }
                        placeholder={ 'https://yourcompany.sharepoint.com/...' }
                    />
                </div>

                { requireDocumentName && (
                    <div className={ 'sharepoint-link-dialog-field' }>
                        <label htmlFor={ 'sharepoint-link-dialog-name' }>Document Name</label>
                        <input
                            id={ 'sharepoint-link-dialog-name' }
                            type={ 'text' }
                            value={ documentName }
                            onChange={ e => {
                                setNameTouched( true );
                                setDocumentName( e.target.value );
                            } }
                            placeholder={ 'Enter document name' }
                        />
                    </div>
                ) }

                { errorMessage && (
                    <Message negative icon={ 'warning circle' } content={ errorMessage } />
                ) }
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={ handleClose } disabled={ isSaving } content={ 'Cancel' } />
                <Button
                    positive
                    icon={ 'linkify' }
                    labelPosition={ 'right' }
                    content={ isSaving ? 'Saving...' : 'Add link' }
                    loading={ isSaving }
                    disabled={ isSaving || !link.trim() }
                    onClick={ handleSubmit }
                />
            </Modal.Actions>
        </Modal>
    );
};

export default SharepointLinkDialog;
